import React, { useEffect, useState } from "react";
import SendTxTemplate from "./components/SendTxTemplate";
import { useWallet } from "../../../hooks/useWallet";
import { Button, ButtonSize } from "../../../components/Button";
import { ButtonVariant } from "../../../config/colors";
import ButtonsInfosTxSenderReceiver from "./components/ButtonsInfosTxSenderReceiver";
import { formatAmount, formatAmountWithSATS, formatNumberWithCommas, localeFormatNumber } from "../../../utils/Utils";
import { useTransaction } from "../../../hooks/useTransaction";
import { PaymentKind, WalletServerInvoiceObject } from "../../../repositories/WalletRepository";
import { InputSearch } from "../../../components/InputSearch";
import { useNavigate } from "react-router-dom";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { setError } from "../../../store/slices/transactionSlice";
import { PrepareLnurlPayResponse, PrepareSendPaymentResponse } from "@breeztech/breez-sdk-spark/web";
import { PreparePaymentResult } from "../../../services/transactionService";

export const getAmountNumber = (amount: string) => {
    return Number(amount.replace(/[^0-9]/g, ''));
};

const SendTxAmount: React.FC = () => {
    const navigate = useNavigate();
    const [amountString, setAmountString] = useState('');
    const {error, amount, recipient, scanData, setTransactionAmount, minAmountSpendable, maxAmountSpendable, isRecipientValid, preparePayment, loading} = useTransaction();

    useEffect(() => {
        if(!isRecipientValid)
            navigate('/home/wallet/send/recipient');
    }, []);

    useEffect(() => {
        if(amount && amount > 0)
            setAmountString(formatNumberWithCommas(amount?.toString() ?? ''));
    }, []);

    useEffect(() => {
        setTransactionAmount(getAmountNumber(amountString));
    }, [amountString]);
    
    const handleButtonClick = () => {
        preparePayment()
            .then((prepareResponse: boolean) => {
                if(prepareResponse) {
                    navigate('/home/wallet/send/recap');
                }
            })
            .catch((err: any) => {
                setError(err.message);
            });
    }

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Get raw input value
        const rawValue = e.target.value;
        // Remove any existing commas
        const cleanValue = rawValue.replace(/,/g, '');
        // Format with commas
        const formattedValue = formatNumberWithCommas(cleanValue);

        const valueNumber = getAmountNumber(formattedValue);
                
        if (maxAmountSpendable === 0 || valueNumber.toString().length <= maxAmountSpendable.toString().length)     
        {
            setAmountString(formattedValue);
        }
    };

    return (
        <LayoutWithTopBar
            breadcrumbs={[
                { title: 'Wallet', onClick: () => navigate('/home/wallet') },
                { title: 'Send' }
            ]}
            onClickPrevArrow={() => navigate('/home/wallet/send/recipient')}
        >
            <SendTxTemplate
                onButtonClick={handleButtonClick}
                buttonDisabled={amountString === '' || error !== ''}
                error={error}
                buttonLoading={loading}
            >
                <InputSearch
                    placeholder="Amount"
                    value={amountString}
                    onChange={handleAmountChange}
                    onClear={() => setAmountString('')}
                    inputMode="numeric"
                />

                {
                    scanData?.kind === PaymentKind.WITHDRAW && (
                        <div className="flex flex-row justify-center mt-4">
                            <Button
                                size={ButtonSize.small}
                                onClick={() => setAmountString(formatNumberWithCommas(Math.floor(minAmountSpendable).toString() ?? ''))}
                                variant={ButtonVariant.unselected}
                                type="button"
                            >
                                Min {formatAmountWithSATS(Math.floor(minAmountSpendable) ?? 0)}
                            </Button>
                        </div>
                    )
                }

                { 
                    maxAmountSpendable && maxAmountSpendable > 0 ? (
                        <div className="flex flex-row justify-center mt-4">
                            <Button
                                size={ButtonSize.small}
                                onClick={() => setAmountString(formatNumberWithCommas(Math.floor(maxAmountSpendable).toString() ?? ''))}
                                variant={ButtonVariant.unselected}
                                type="button"
                            >
                                Max {formatAmountWithSATS(Math.floor(maxAmountSpendable) ?? 0)}
                            </Button>
                        </div>
                    ) : null
                }

                {
                    scanData?.lockedFunds && isFutureDate(scanData.lockedFunds.until) && (
                        <div className="flex flex-row justify-center mt-8">
                            <p className="text-brand-red text-center">
                                Your {formatAmount(scanData?.lockedFunds.amountMsat / 1000)} Sats Bonus will be available in {getHoursString(new Date(scanData?.lockedFunds.until ?? ''))}
                            </p>
                        </div>
                    )
                }
                
                <div className="flex flex-row justify-center mt-8">
                    <ButtonsInfosTxSenderReceiver
                        recipient={recipient}
                        clickable={false}
                    />
                </div>
            </SendTxTemplate>
        </LayoutWithTopBar>
    );
}

function isFutureDate(date: string) {
    return new Date(date) > new Date();
}

const getHoursString = (date: Date): string => {
    const hours = Math.floor((date.getTime() - new Date().getTime()) / (1000 * 60 * 60));
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  };

export default SendTxAmount;